import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PerWordResult, RawHit, VerseMeta } from "../core/types";
import { wordColor } from "../core/palette";
import {
  buildParashaRanges,
  findParashaAt,
  type ParashaRange,
} from "../core/parashot";

// Letters per heatmap column. Each column represents a window of POS_BIN
// consecutive Torah letters; words landing in the same column are spatially
// close in the corpus.
export const POS_BIN = 100;

// Backward-compatibility alias.
export const ROW_BIN = POS_BIN;

// Pure helper, exported for unit tests. Transposed layout:
//   row = |d| - dMin              (vertical axis = skip distance)
//   col = floor(p / POS_BIN)      (horizontal axis = Torah position)
// Backward hits (d < 0) collapse onto positive |d| rows.
export function hitToCell(
  p: number,
  d: number,
  dMin: number,
): { row: number; col: number } {
  return {
    row: Math.abs(d) - dMin,
    col: Math.floor(p / POS_BIN),
  };
}

export interface SelectedHit {
  wordIndex: number;
  hitIndex: number;
}

export interface HeatMapProps {
  results: PerWordResult[];
  N: number;
  dMin: number;
  dMax: number;
  selected: SelectedHit | null;
  onSelect: (sel: SelectedHit) => void;
  verses: VerseMeta[];
  // Word indices that should be visually muted (not drawn in cells, not
  // counted in density). Mute-state lives in App so all panes stay in sync.
  mutedWords?: ReadonlySet<number>;
}

interface CellHits {
  // Map word index -> first hit index in that cell, kept stable across
  // re-draws so click-to-select picks a deterministic result.
  byWord: Map<number, number>;
}

interface CellMap {
  cells: Map<number, CellHits>;
  // Active original column indices (cols that contain at least one hit),
  // sorted ascending. Used for compact-mode rendering.
  activeCols: number[];
  // Distinct word indices present in each column, sorted ascending so the
  // density-strip stripes always paint in the same order across renders.
  colWords: Map<number, number[]>;
}

function buildCellMap(
  results: PerWordResult[],
  dMin: number,
  dMax: number,
  numCols: number,
  muted?: ReadonlySet<number>,
): CellMap {
  const cells = new Map<number, CellHits>();
  const colWords = new Map<number, Set<number>>();
  const numRows = dMax - dMin + 1;
  for (let wi = 0; wi < results.length; wi++) {
    if (muted && muted.has(wi)) continue;
    const word = results[wi]!;
    for (let hi = 0; hi < word.hits.length; hi++) {
      const h = word.hits[hi]!;
      const absD = Math.abs(h.d);
      if (absD < dMin || absD > dMax) continue;
      const { row, col } = hitToCell(h.p, h.d, dMin);
      if (row < 0 || row >= numRows) continue;
      if (col < 0 || col >= numCols) continue;
      const key = row * numCols + col;
      let cell = cells.get(key);
      if (!cell) {
        cell = { byWord: new Map() };
        cells.set(key, cell);
      }
      if (!cell.byWord.has(wi)) cell.byWord.set(wi, hi);
      let cw = colWords.get(col);
      if (!cw) {
        cw = new Set();
        colWords.set(col, cw);
      }
      cw.add(wi);
    }
  }
  const activeCols = Array.from(colWords.keys()).sort((a, b) => a - b);
  const colWordsArr = new Map<number, number[]>();
  for (const [c, set] of colWords) {
    colWordsArr.set(c, Array.from(set).sort((a, b) => a - b));
  }
  return { cells, activeCols, colWords: colWordsArr };
}

function findVerseAt(verses: VerseMeta[], pos: number): VerseMeta | null {
  let lo = 0;
  let hi = verses.length - 1;
  let result = verses[0] ?? null;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const v = verses[mid]!;
    if (v.compactStart <= pos) {
      result = v;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}

interface LayoutItem {
  // "cell" items map a canvas x-range to an original column index.
  // "gap" items represent a collapsed empty range and have no col.
  kind: "cell" | "gap";
  startX: number;
  width: number;
  originalCol?: number;
}

interface Layout {
  items: LayoutItem[];
  canvasWidth: number;
}

const GAP_W = 4;

function buildLayout(
  numCols: number,
  cellW: number,
  compact: boolean,
  activeCols: number[],
): Layout {
  const items: LayoutItem[] = [];
  let x = 0;
  if (!compact || activeCols.length === 0) {
    for (let c = 0; c < numCols; c++) {
      items.push({ kind: "cell", startX: x, width: cellW, originalCol: c });
      x += cellW;
    }
  } else {
    let prev = Number.NEGATIVE_INFINITY;
    for (const c of activeCols) {
      if (prev !== Number.NEGATIVE_INFINITY && c !== prev + 1) {
        items.push({ kind: "gap", startX: x, width: GAP_W });
        x += GAP_W;
      }
      items.push({ kind: "cell", startX: x, width: cellW, originalCol: c });
      x += cellW;
      prev = c;
    }
  }
  return { items, canvasWidth: x };
}

function findLayoutItemAtX(items: LayoutItem[], x: number): LayoutItem | null {
  // Binary search by startX.
  let lo = 0;
  let hi = items.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const it = items[mid]!;
    if (x < it.startX) hi = mid - 1;
    else if (x >= it.startX + it.width) lo = mid + 1;
    else return it;
  }
  return null;
}

interface TooltipState {
  x: number;
  y: number;
  row: number;
  col: number;
  words: Array<{ wordIndex: number; canonical: string; hit: RawHit }>;
  // Which canvas the tooltip belongs to. The tooltip is rendered inside
  // the matching wrapper so its absolute coordinates work regardless of
  // sticky positioning.
  origin: "header" | "grid";
}

const HEADER_GAP_PX = 2;
const MIN_CELL_SIZE = 2;
const MAX_CELL_SIZE = 40;
const PARASHA_STRIP_PX = 22;
const DENSITY_MIN_PX = 18;

// Track devicePixelRatio with a media-query listener so the canvas redraws
// at the right resolution when the user changes browser zoom or moves the
// window between displays.
function useDevicePixelRatio(): number {
  const [dpr, setDpr] = useState(
    typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const onChange = () => setDpr(window.devicePixelRatio || 1);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [dpr]);
  return dpr;
}

export function HeatMap(props: HeatMapProps): JSX.Element {
  const { results, N, dMin, dMax, selected, onSelect, verses, mutedWords } =
    props;
  const scrollRef = useRef<HTMLDivElement>(null);
  // Header lives in its own canvas so it can be position: sticky inside the
  // scroll container and stay visible while the user scrolls the grid.
  const headerCanvasRef = useRef<HTMLCanvasElement>(null);
  const gridCanvasRef = useRef<HTMLCanvasElement>(null);
  const [paneWidth, setPaneWidth] = useState(800);
  const [paneHeight, setPaneHeight] = useState(360);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [compact, setCompact] = useState(true);
  // Minimum distinct-word hits a column must have to be shown. 1 == every
  // column with any hit; ≥2 isolates the multi-word "interesting" columns.
  const [minHits, setMinHits] = useState(1);
  const [manualCellSize, setManualCellSize] = useState<number | null>(null);
  const dpr = useDevicePixelRatio();

  const numRows = Math.max(1, dMax - dMin + 1);
  const numCols = Math.max(1, Math.ceil(N / POS_BIN));

  const parashaRanges = useMemo<ParashaRange[]>(
    () => buildParashaRanges(verses, N),
    [verses, N],
  );

  const cellMap = useMemo(
    () => buildCellMap(results, dMin, dMax, numCols, mutedWords),
    [results, dMin, dMax, numCols, mutedWords],
  );

  // Filter active columns by min-hits threshold. minHits === 1 keeps every
  // column with any hit (classic compact behaviour); higher values reveal
  // only columns where multiple distinct words co-occur.
  const visibleActiveCols = useMemo(
    () =>
      minHits <= 1
        ? cellMap.activeCols
        : cellMap.activeCols.filter(
            (c) => (cellMap.colWords.get(c)?.length ?? 0) >= minHits,
          ),
    [cellMap, minHits],
  );

  // Auto-fit cell size: pick the largest square that fits the visible pane,
  // capped so cells stay legible. The user can override with the zoom
  // controls; their choice always wins until they hit "fit".
  // minHits ≥ 2 implicitly enables compaction — there's no useful way to
  // "filter cols" while still showing the full uncompacted layout.
  const compactActive =
    (compact || minHits >= 2) && visibleActiveCols.length > 0;
  const colsToShow = compactActive ? visibleActiveCols.length : numCols;
  const rowsToShow = numRows + 1;
  const cellByHeight = Math.max(MIN_CELL_SIZE, Math.floor(paneHeight / rowsToShow));
  const cellByWidth = Math.max(
    MIN_CELL_SIZE,
    Math.floor(paneWidth / Math.max(1, colsToShow)),
  );
  const autoCellSize = Math.max(
    MIN_CELL_SIZE,
    Math.min(cellByHeight, cellByWidth, 16),
  );
  const cellSize = manualCellSize ?? autoCellSize;

  const layout = useMemo(
    () => buildLayout(numCols, cellSize, compactActive, visibleActiveCols),
    [numCols, cellSize, compactActive, visibleActiveCols],
  );

  // Parasha strip is a fixed-height ribbon above the density header. It
  // shows boundaries between Torah portions with rotated labels at each
  // start so the user can quickly navigate to a familiar weekly reading.
  const parashaH = parashaRanges.length > 0 ? PARASHA_STRIP_PX : 0;
  // Density header is at least DENSITY_MIN_PX tall so multi-hit count
  // badges remain readable even when the grid uses tiny cells.
  const headerH = Math.max(cellSize, DENSITY_MIN_PX);
  const gridH = cellSize * numRows;
  // Within the (separate) header canvas: parasha at y=0..parashaH, gap,
  // density at y=headerY0..+headerH.
  const headerY0 = parashaH > 0 ? parashaH + HEADER_GAP_PX : 0;
  const headerCanvasH = headerY0 + headerH;
  const gridCanvasH = gridH;
  const canvasWidth = Math.max(1, layout.canvasWidth);

  // Track scroll-pane size so the auto-fit cellSize keeps up with browser
  // resizes and zoom level changes. ResizeObserver fires on both.
  useLayoutEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const update = () => {
      setPaneWidth(el.clientWidth);
      setPaneHeight(el.clientHeight);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep minHits within [1..numWords] when the user adds/removes words.
  const numWordsTotal = Math.max(1, results.length);
  useEffect(() => {
    setMinHits((n) => Math.min(numWordsTotal, Math.max(1, n)));
  }, [numWordsTotal]);

  const zoomIn = () =>
    setManualCellSize((s) => Math.min(MAX_CELL_SIZE, (s ?? autoCellSize) + 1));
  const zoomOut = () =>
    setManualCellSize((s) => Math.max(MIN_CELL_SIZE, (s ?? autoCellSize) - 1));
  const zoomFit = () => setManualCellSize(null);

  // Ctrl/Cmd + wheel = zoom (matches the canvas-app convention). React's
  // synthetic wheel handler runs in a passive listener since React 17, so
  // we attach a native non-passive listener to actually prevent the page
  // from scrolling/zooming when zooming the heatmap.
  const autoCellSizeRef = useRef(autoCellSize);
  autoCellSizeRef.current = autoCellSize;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = (ev: WheelEvent) => {
      if (!(ev.ctrlKey || ev.metaKey)) return;
      ev.preventDefault();
      const delta = ev.deltaY > 0 ? -1 : 1;
      setManualCellSize((s) => {
        const cur = s ?? autoCellSizeRef.current;
        return Math.max(MIN_CELL_SIZE, Math.min(MAX_CELL_SIZE, cur + delta));
      });
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  // Painting. Two canvases:
  //   - headerCanvasRef draws the parasha strip + density strip (sticky).
  //   - gridCanvasRef draws gridlines + cells + selection (scrolls).
  // Each canvas allocates its bitmap at devicePixelRatio for crisp output
  // at any browser zoom level; CSS sizing remains in CSS pixels.

  // 1. Header canvas paint.
  useEffect(() => {
    const canvas = headerCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = Math.max(1, Math.round(canvasWidth * dpr));
    canvas.height = Math.max(1, Math.round(headerCanvasH * dpr));
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${headerCanvasH}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = "#0a0c12";
    ctx.fillRect(0, 0, canvasWidth, headerCanvasH);

    const numWords = Math.max(
      1,
      results.length - (mutedWords?.size ?? 0),
    );

    // Parasha strip with alternating background tints + rotated labels.
    if (parashaH > 0) {
      ctx.save();
      const labelFont = `${Math.min(11, Math.max(9, parashaH - 8))}px ui-sans-serif, system-ui, sans-serif`;
      ctx.font = labelFont;
      ctx.textBaseline = "alphabetic";

      let prevParashaIndex = -1;
      for (const it of layout.items) {
        if (it.kind === "gap") {
          ctx.fillStyle = "#1b2030";
          ctx.fillRect(it.startX, 0, it.width, parashaH);
          continue;
        }
        const c = it.originalCol!;
        const startPos = c * POS_BIN;
        const range = findParashaAt(parashaRanges, startPos);
        const idx = range ? parashaRanges.indexOf(range) : -1;
        const tint = idx % 2 === 0 ? "#1f2533" : "#262d3f";
        ctx.fillStyle = idx >= 0 ? tint : "#11141d";
        ctx.fillRect(it.startX, 0, it.width, parashaH);

        if (range && idx !== prevParashaIndex) {
          ctx.fillStyle = "#5b6477";
          ctx.fillRect(it.startX, 0, 1, parashaH);
          if (cellSize >= 4) {
            ctx.save();
            ctx.translate(it.startX + Math.min(cellSize - 1, 12), parashaH - 4);
            ctx.rotate(-Math.PI / 2);
            ctx.fillStyle = "#cfd6e6";
            ctx.fillText(range.ref.name, 0, 0);
            ctx.restore();
          }
          prevParashaIndex = idx;
        } else if (range) {
          prevParashaIndex = idx;
        } else {
          prevParashaIndex = -1;
        }
      }
      ctx.restore();
    }

    // Density strip — designed to make multi-hit columns pop:
    //   • Single-hit columns: faint solid swatch in the word's color.
    //   • Multi-hit columns: word-color stripes (so the user immediately
    //     sees *which* words co-occur) on a saturated gold backplate +
    //     a numeric badge with the distinct-word count when there's
    //     room.
    ctx.fillStyle = "#0d1017";
    ctx.fillRect(0, headerY0, canvasWidth, headerH);

    const badgeFont = `${Math.min(11, Math.max(9, Math.floor(headerH * 0.65)))}px ui-sans-serif, system-ui, sans-serif`;

    for (const it of layout.items) {
      if (it.kind === "gap") {
        ctx.fillStyle = "#1b2030";
        ctx.fillRect(it.startX, headerY0, it.width, headerH);
        continue;
      }
      const wordsHere = cellMap.colWords.get(it.originalCol!) ?? [];
      const count = wordsHere.length;
      if (count === 0) continue;

      if (count === 1) {
        ctx.fillStyle = wordColor(wordsHere[0]!, 0.35);
        ctx.fillRect(it.startX, headerY0, it.width, headerH);
        continue;
      }

      // Multi-hit: a warm gold backplate scaled by saturation with the
      // distinct-word ratio, then word-color stripes inside it.
      const saturation = Math.min(1, count / Math.max(2, numWords));
      const bgAlpha = 0.35 + 0.55 * saturation;
      ctx.fillStyle = `rgba(218, 165, 32, ${bgAlpha.toFixed(3)})`;
      ctx.fillRect(it.startX, headerY0, it.width, headerH);

      const stripeW = Math.max(1, Math.floor(it.width / count));
      const stripesTotalW = stripeW * count;
      const stripesX0 = it.startX + Math.floor((it.width - stripesTotalW) / 2);
      const stripesY0 = headerY0 + 1;
      const stripesH = headerH - 2;
      for (let i = 0; i < count; i++) {
        ctx.fillStyle = wordColor(wordsHere[i]!);
        ctx.fillRect(stripesX0 + i * stripeW, stripesY0, stripeW, stripesH);
      }

      // Count badge — only when the cell is wide and tall enough to show
      // a legible glyph without overlapping its neighbours.
      if (it.width >= 12 && headerH >= 14) {
        ctx.save();
        ctx.font = badgeFont;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        // Outline for legibility on top of the colored stripes.
        ctx.lineWidth = 3;
        ctx.strokeStyle = "rgba(0, 0, 0, 0.85)";
        ctx.fillStyle = "#ffffff";
        const cx = it.startX + it.width / 2;
        const cy = headerY0 + headerH / 2;
        ctx.strokeText(String(count), cx, cy);
        ctx.fillText(String(count), cx, cy);
        ctx.restore();
      }
    }
  }, [
    layout,
    cellMap,
    cellSize,
    headerH,
    headerY0,
    headerCanvasH,
    parashaH,
    parashaRanges,
    canvasWidth,
    results.length,
    mutedWords,
    dpr,
  ]);

  // 2. Grid canvas paint.
  useEffect(() => {
    const canvas = gridCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = Math.max(1, Math.round(canvasWidth * dpr));
    canvas.height = Math.max(1, Math.round(gridCanvasH * dpr));
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${gridCanvasH}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = "#0a0c12";
    ctx.fillRect(0, 0, canvasWidth, gridCanvasH);

    // Subtle horizontal gridlines every 10 skip rows.
    ctx.fillStyle = "#11141d";
    for (let r = 0; r < numRows; r += 10) {
      ctx.fillRect(0, r * cellSize, canvasWidth, 1);
    }

    // Gap separators (vertical).
    ctx.fillStyle = "#11141d";
    for (const it of layout.items) {
      if (it.kind === "gap") {
        ctx.fillRect(it.startX, 0, it.width, gridCanvasH);
        ctx.fillStyle = "#2a3244";
        ctx.fillRect(
          it.startX + Math.floor(it.width / 2),
          0,
          1,
          gridCanvasH,
        );
        ctx.fillStyle = "#11141d";
      }
    }

    // Cells.
    for (const it of layout.items) {
      if (it.kind !== "cell") continue;
      const c = it.originalCol!;
      for (let r = 0; r < numRows; r++) {
        const cell = cellMap.cells.get(r * numCols + c);
        if (!cell) continue;
        const x = it.startX;
        const y = r * cellSize;
        const wordIndices = Array.from(cell.byWord.keys());
        if (wordIndices.length === 1) {
          ctx.fillStyle = wordColor(wordIndices[0]!);
          ctx.fillRect(x, y, cellSize, cellSize);
        } else {
          const stripeW = Math.max(1, Math.floor(cellSize / wordIndices.length));
          for (let i = 0; i < wordIndices.length; i++) {
            ctx.fillStyle = wordColor(wordIndices[i]!);
            ctx.fillRect(x + i * stripeW, y, stripeW, cellSize);
          }
        }
      }
    }

    // Selection outline.
    if (selected) {
      const word = results[selected.wordIndex];
      const hit = word?.hits[selected.hitIndex];
      if (word && hit) {
        const { row, col } = hitToCell(hit.p, hit.d, dMin);
        const it = layout.items.find(
          (item) => item.kind === "cell" && item.originalCol === col,
        );
        if (it && row >= 0 && row < numRows) {
          const x = it.startX;
          const y = row * cellSize;
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1;
          ctx.strokeRect(x - 0.5, y - 0.5, cellSize + 1, cellSize + 1);
        }
      }
    }
  }, [
    layout,
    cellMap,
    cellSize,
    numRows,
    numCols,
    canvasWidth,
    gridCanvasH,
    results,
    mutedWords,
    selected,
    dMin,
    dpr,
  ]);

  // Hit test uses canvas-local coordinates. Each canvas (header / grid) has
  // its own y origin so the math is straightforward.
  function hitTestGrid(
    cx: number,
    cy: number,
  ): { row: number; col: number; cell: CellHits | undefined } | null {
    const row = Math.floor(cy / cellSize);
    if (row < 0 || row >= numRows) return null;
    const it = findLayoutItemAtX(layout.items, cx);
    if (!it || it.kind !== "cell") return null;
    const col = it.originalCol!;
    const cell = cellMap.cells.get(row * numCols + col);
    return { row, col, cell };
  }

  // Find the cell in column `col` with the maximum |d|. Used by the header
  // click "auto-pick" feature so the matrix view opens at the widest row
  // that still encloses every cluster word in that column.
  function pickWidestInColumn(
    col: number,
  ): { wordIndex: number; hitIndex: number } | null {
    let best: { wordIndex: number; hitIndex: number; absD: number } | null = null;
    for (let wi = 0; wi < results.length; wi++) {
      if (mutedWords && mutedWords.has(wi)) continue;
      const word = results[wi]!;
      for (let hi = 0; hi < word.hits.length; hi++) {
        const h = word.hits[hi]!;
        const absD = Math.abs(h.d);
        if (absD < dMin || absD > dMax) continue;
        const c = Math.floor(h.p / POS_BIN);
        if (c !== col) continue;
        if (!best || absD > best.absD) {
          best = { wordIndex: wi, hitIndex: hi, absD };
        }
      }
    }
    return best;
  }

  // Header canvas: hovering shows a column-summary tooltip; click picks the
  // widest |d| hit in that column so the matrix opens wide enough to fit
  // every co-located word.
  const onHeaderMouseMove = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const cx = ev.clientX - rect.left;
    const cy = ev.clientY - rect.top;
    const it = findLayoutItemAtX(layout.items, cx);
    if (!it || it.kind !== "cell") {
      setTooltip(null);
      return;
    }
    const col = it.originalCol!;
    const summaryWords: TooltipState["words"] = [];
    for (let wi = 0; wi < results.length; wi++) {
      if (mutedWords && mutedWords.has(wi)) continue;
      const word = results[wi]!;
      for (let hi = 0; hi < word.hits.length; hi++) {
        const h = word.hits[hi]!;
        const absD = Math.abs(h.d);
        if (absD < dMin || absD > dMax) continue;
        if (Math.floor(h.p / POS_BIN) !== col) continue;
        summaryWords.push({
          wordIndex: wi,
          canonical: word.canonical,
          hit: h,
        });
        break;
      }
    }
    setTooltip({
      x: cx,
      y: cy,
      row: -1,
      col,
      words: summaryWords,
      origin: "header",
    });
  };

  const onHeaderClick = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const cx = ev.clientX - rect.left;
    const it = findLayoutItemAtX(layout.items, cx);
    if (!it || it.kind !== "cell") return;
    const pick = pickWidestInColumn(it.originalCol!);
    if (pick) onSelect(pick);
  };

  // Grid canvas: hover-tooltip on cells with content; click selects the
  // first word in the cell.
  const onGridMouseMove = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const cx = ev.clientX - rect.left;
    const cy = ev.clientY - rect.top;
    const ht = hitTestGrid(cx, cy);
    if (!ht || !ht.cell) {
      setTooltip(null);
      return;
    }
    const words = Array.from(ht.cell.byWord.entries()).map(
      ([wordIndex, hitIndex]) => {
        const w = results[wordIndex]!;
        return {
          wordIndex,
          canonical: w.canonical,
          hit: w.hits[hitIndex]!,
        };
      },
    );
    setTooltip({
      x: cx,
      y: cy,
      row: ht.row,
      col: ht.col,
      words,
      origin: "grid",
    });
  };

  const onGridClick = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const cx = ev.clientX - rect.left;
    const cy = ev.clientY - rect.top;
    const ht = hitTestGrid(cx, cy);
    if (!ht || !ht.cell) return;
    const first = ht.cell.byWord.entries().next().value;
    if (!first) return;
    const [wordIndex, hitIndex] = first as [number, number];
    onSelect({ wordIndex, hitIndex });
  };

  const onCanvasMouseLeave = () => setTooltip(null);

  if (results.length === 0) {
    return (
      <div className="heatmap-empty">
        Type words on the left to see them light up across the Torah.
      </div>
    );
  }

  return (
    <div className="heatmap-container">
      <div className="heatmap-toolbar">
        <span className="heatmap-meta">
          <span>
            skip {dMin}…{dMax} · {numRows} rows × {colsToShow}
            {compactActive ? "" : `/${numCols}`} cols · {POS_BIN} letters/col
          </span>
          <label className="heatmap-toggle">
            <input
              type="checkbox"
              checked={compact}
              onChange={(e) => setCompact(e.target.checked)}
            />
            compact (hide empty positions)
          </label>
          <span
            className="heatmap-minhits"
            title="Show only columns with at least this many distinct words"
          >
            <span className="heatmap-minhits-label">min hits</span>
            <button
              type="button"
              onClick={() => setMinHits((n) => Math.max(1, n - 1))}
              disabled={minHits <= 1}
              aria-label="Decrease min hits"
            >
              −
            </button>
            <span className="heatmap-minhits-value">≥ {minHits}</span>
            <button
              type="button"
              onClick={() =>
                setMinHits((n) => Math.min(numWordsTotal, n + 1))
              }
              disabled={minHits >= numWordsTotal}
              aria-label="Increase min hits"
            >
              +
            </button>
          </span>
        </span>
        <span className="toolbar-zoom" title="Ctrl/⌘ + scroll to zoom">
          <button onClick={zoomOut} aria-label="Zoom out">−</button>
          <span className="zoom-readout">{cellSize}px</span>
          <button onClick={zoomIn} aria-label="Zoom in">+</button>
          <button onClick={zoomFit} aria-label="Fit to pane">fit</button>
        </span>
      </div>
      <div className="heatmap-scroll" ref={scrollRef}>
        <div className="heatmap-canvas-wrap" style={{ width: canvasWidth }}>
          <div
            className="heatmap-header-sticky"
            style={{ width: canvasWidth, height: headerCanvasH }}
          >
            <canvas
              ref={headerCanvasRef}
              onMouseMove={onHeaderMouseMove}
              onMouseLeave={onCanvasMouseLeave}
              onClick={onHeaderClick}
            />
            {tooltip && tooltip.origin === "header" && (
              <Tooltip
                tooltip={tooltip}
                dMin={dMin}
                verses={verses}
                parashaRanges={parashaRanges}
              />
            )}
          </div>
          <div
            className="heatmap-grid-wrap"
            style={{ width: canvasWidth, height: gridCanvasH }}
          >
            <canvas
              ref={gridCanvasRef}
              onMouseMove={onGridMouseMove}
              onMouseLeave={onCanvasMouseLeave}
              onClick={onGridClick}
            />
            {tooltip && tooltip.origin === "grid" && (
              <Tooltip
                tooltip={tooltip}
                dMin={dMin}
                verses={verses}
                parashaRanges={parashaRanges}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Tooltip(props: {
  tooltip: TooltipState;
  dMin: number;
  verses: VerseMeta[];
  parashaRanges: ParashaRange[];
}): JSX.Element {
  const { tooltip, dMin, verses, parashaRanges } = props;
  const startPos = tooltip.col * POS_BIN;
  const verse = findVerseAt(verses, startPos);
  const parasha = findParashaAt(parashaRanges, startPos);
  const isHeader = tooltip.row < 0;
  return (
    <div
      className="heatmap-tooltip"
      style={{
        left: Math.min(tooltip.x + 12, 600),
        top: tooltip.y + 12,
      }}
    >
      <div>
        {isHeader
          ? `column ${tooltip.col} · click to open widest skip`
          : `skip ${tooltip.row + dMin}`}{" "}
        · letters {startPos.toLocaleString()}…
        {(startPos + POS_BIN - 1).toLocaleString()}
      </div>
      {parasha && (
        <div className="tooltip-parasha">
          <span dir="rtl" lang="he">{parasha.ref.nameHebrew}</span>{" "}
          <span className="muted">({parasha.ref.name})</span>
        </div>
      )}
      {verse && (
        <div className="tooltip-verse">
          {verse.book} {verse.chapter}:{verse.verse}
        </div>
      )}
      <ul>
        {tooltip.words.map((w) => (
          <li key={w.wordIndex}>
            <span
              className="swatch"
              style={{ backgroundColor: wordColor(w.wordIndex) }}
            />
            <span dir="rtl" lang="he">
              {w.canonical}
            </span>{" "}
            <span className="muted">
              p={w.hit.p.toLocaleString()} d={w.hit.d}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
